import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Iteration 59 — cache-busting for auth + bank-status endpoints. The
// Payments plane must never return a stale answer to a mobile browser
// that just returned from Stripe onboarding, so we tack on both a
// `?t=<ms>` query param AND a `Cache-Control: no-store` request header.
// The list is intentionally narrow so cachable public feeds (browse,
// shop) still get browser + CDN caching.
const NO_STORE_PATHS = [
  "/auth/me",
  "/wallet/connect/status",
  "/dashboard/parent",
];
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  const url = config.url || "";
  if (method === "get" && NO_STORE_PATHS.some((p) => url === p || url.startsWith(`${p}?`) || url.startsWith(`${p}/`))) {
    config.params = { ...(config.params || {}), _t: Date.now() };
    config.headers = {
      ...(config.headers || {}),
      "Cache-Control": "no-store, no-cache, must-revalidate, max-age=0",
      Pragma: "no-cache",
    };
  }
  return config;
});

export default api;

const TEMPORARILY_UNAVAILABLE_MESSAGE = "Service temporarily unavailable. Please refresh or try again in a moment.";
const HTML_ERROR_PATTERN = /<!doctype\s+html|<\/?\s*(html|head|body|title|h1|p|div|span|script)\b/i;

const looksLikeHtmlError = (value) => typeof value === "string" && HTML_ERROR_PATTERN.test(value);
const safeErrorText = (value) => {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text) return null;
  return looksLikeHtmlError(text) ? TEMPORARILY_UNAVAILABLE_MESSAGE : text;
};

export function formatApiError(err) {
  const data = err?.response?.data;
  const detail = data?.detail;
  const dataText = safeErrorText(data);
  const detailText = safeErrorText(detail);
  if (detailText) return detailText;
  if (detail == null) {
    if (dataText) return dataText;
    // Some upstream endpoints return the error envelope at the top level
    // (e.g. `{error:"insufficient_wallet_balance", wallet_available_pennies:0}`).
    // Translate the known short-codes into friendly copy; otherwise fall
    // through to the axios message.
    if (data && typeof data === "object") {
      const code = typeof data.error === "string" ? data.error : "";
      const messageText = safeErrorText(data.message);
      if (messageText) return messageText;
      const FRIENDLY = {
        insufficient_wallet_balance: "Not enough in your household wallet — top it up and try again.",
        insufficient_funds: "Not enough in your wallet — top it up and try again.",
        rate_limited: "Please wait a moment before trying again.",
        forbidden: "That action isn't allowed for your account.",
        not_found: "We couldn't find that record.",
      };
      if (code && FRIENDLY[code]) return FRIENDLY[code];
      if (code) return code.replace(/_/g, " ");
    }
    return err?.message || "Something went wrong.";
  }
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  const detailMsg = safeErrorText(detail?.msg);
  if (detailMsg) return detailMsg;
  // Handle upstream moderation-style objects like {error:"safety_rejected", message:"..."}
  const detailMessage = safeErrorText(detail?.message);
  if (detailMessage) return detailMessage;
  if (detail && typeof detail === "object") {
    try {
      const serialized = JSON.stringify(detail);
      return looksLikeHtmlError(serialized) ? TEMPORARILY_UNAVAILABLE_MESSAGE : serialized;
    } catch { /* fall through */ }
  }
  return String(detail);
}
